/**
 * Named Tailwind class recipes — keep in sync with src/config/colors.js and tailwind-theme.cjs.
 */
export const themeClasses = {
  layout: {
    mainContentMax: 'mx-auto w-full min-w-0 max-w-[1400px]',
    modalOverlay: 'bg-black/60',
    modalPanel:
      'border border-border-light bg-background-card text-text-primary shadow-elevation-5 rounded-lg',
  },
  appKit: {
    sectionShell:
      'rounded-md border border-border-light bg-background-card text-text-primary shadow-elevation-3',
    sectionHeaderStack: 'flex flex-col gap-1.5',
    sectionTitle: 'text-lg font-semibold tracking-tight text-text-primary',
    sectionSubtitle: 'text-sm text-text-secondary',
    sectionEyebrow: 'font-mono text-xs uppercase tracking-wider text-text-tertiary',
    emptyDashed:
      'rounded-lg border border-dashed border-border-light bg-background-primary px-6 py-10 text-center',
    emptyIconTile:
      'mx-auto mb-4 flex size-14 items-center justify-center rounded-full border border-border-light bg-canvas-soft text-text-secondary',
    statIconTile: 'rounded-full border border-border-light p-3 sm:p-4',
    heroBand: 'relative overflow-hidden rounded-lg',
    meshGradientHero: 'mesh-gradient-hero',
  },
  semantic: {
    riskHigh: 'bg-danger-500',
    riskHighSoft: 'bg-danger-500/10 border-danger-500/30 text-danger-500',
    riskMedium: 'bg-info-500',
    riskMediumSoft: 'bg-info-500/10 border-info-500/30 text-info-500',
    riskLow: 'bg-success-500',
    riskLowSoft: 'bg-success-500/10 border-success-500/30 text-success-500',
    riskUnknown: 'bg-warning-500',
    riskUnknownSoft: 'bg-warning-500/10 border-warning-500/30 text-warning-500',
    priorityHigh: 'bg-danger-500',
    priorityNormal: 'bg-info-500',
    priorityLow: 'bg-success-500',
    priorityOther: 'bg-warning-500',
  },
  chart: {
    barTrack: 'bg-background-tertiary rounded-full h-2',
    industryDot: 'bg-brand-500',
    industryBar: 'bg-brand-500',
  },
  nav: {
    activeRow: 'border-l-[3px] border-primary-500 bg-primary-100 text-text-primary',
    inactiveRow: 'border-l-[3px] border-transparent text-text-secondary hover:bg-primary-50',
  },
  landing: {
    page: 'min-h-screen bg-background-primary text-text-primary',
    section: 'py-16 sm:py-24 lg:py-28',
    container: 'mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8',
    containerWide: 'mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8',
    eyebrow: 'font-mono text-xs uppercase tracking-wider text-text-tertiary',
    heroTitle: 'text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-text-primary',
    heroSubtitle: 'text-lg sm:text-xl text-text-secondary max-w-2xl',
    featureCard:
      'rounded-lg border border-border-light bg-background-card p-6 shadow-elevation-3 transition-colors hover:border-border-medium',
    ctaBand:
      'relative overflow-hidden rounded-xl border border-border-light bg-background-secondary p-8 sm:p-12 shadow-elevation-4',
    navBar: 'sticky top-0 z-50 border-b border-border-light landing-nav-blur',
    stepNumber:
      'flex size-10 shrink-0 items-center justify-center rounded-full border border-border-light bg-background-card font-mono text-sm text-text-secondary',
    showcaseGrid: 'grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch',
    showcaseCard: 'flex flex-col rounded-md border border-border-light bg-background-card p-6 shadow-elevation-3',
    showcaseMapCol: 'lg:col-span-2 flex flex-col min-h-0',
    showcaseSideCol: 'flex flex-col min-h-0',
  },
};

export function priorityBarClasses(priority) {
  const map = {
    high: themeClasses.semantic.priorityHigh,
    normal: themeClasses.semantic.priorityNormal,
    low: themeClasses.semantic.priorityLow,
  };
  return map[priority] || themeClasses.semantic.priorityOther;
}

export const formControlClasses = {
  label: 'block text-sm font-medium text-text-primary mb-2',
  input:
    'flex h-10 w-full rounded-sm border border-border-light bg-background-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  select:
    'flex h-10 w-full rounded-sm border border-border-light bg-background-secondary px-3 py-2 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  textarea:
    'flex min-h-[80px] w-full rounded-sm border border-border-light bg-background-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  error: 'mt-1 text-sm text-danger-500',
};

export function priorityIconTileClasses(priority) {
  const map = {
    high: themeClasses.semantic.riskHighSoft,
    normal: themeClasses.semantic.riskMediumSoft,
    low: themeClasses.semantic.riskLowSoft,
  };
  return map[priority] || themeClasses.semantic.riskUnknownSoft;
}
